// @ts-nocheck -- copied verbatim from the application being replaced.
//
// Quarantined, not exempted. This file has not been rewritten to this project's
// settings and does not pass them; checking it would report errors nobody is
// going to fix in place, and loosening the settings to accommodate it would
// apply that to code written here too. Vite still compiles it -- esbuild strips
// types without checking them.
//
// Delete this line when the file is rewritten. It is the marker for what is left
import { logger } from '@/shared/observability/logger';
// ============================================================================
// COLLECTION SERVICE (API-INTEGRATED)
// ============================================================================
// Wrapper service that uses the API backend for collections
// Maintains backward compatibility with existing components

import { CollectionApiService } from './collection-api.service';

// Re-export types for backward compatibility
export type {
  Collection,
  CollectionItem,
  CreateCollectionRequest,
  AddItemToCollectionRequest,
  CollectionItemsResponse,
  ApiResponse,
} from './collection-api.service';

// ============================================================================
// COLLECTION SERVICE CLASS (API Wrapper)
// ============================================================================

class CollectionServiceClass {
  /**
   * Get all collections for the current user
   */
  async getCollections() {
    const response = await CollectionApiService.getCollections();
    
    if (!response.success) {
      logger.error('[CollectionService] API error:', response.error);
      throw new Error(response.error || 'Failed to load collections');
    }
    
    // Defensive check: ensure we always return an array
    const data = response.data;
    if (!Array.isArray(data)) {
      logger.error('[CollectionService] Expected array but got:', typeof data, data);
      return [];
    }
    
    // Normalize API response: ensure 'name' field exists for all collections
    return data.map(collection => {
      if (!collection.name && (collection as any).collectionName) {
        collection.name = (collection as any).collectionName;
      }
      return collection;
    });
  }

  /**
   * Get a specific collection by ID
   */
  async getCollection(collectionId: string) {
    const collections = await this.getCollections();
    const collection = collections.find((c) => c.id === collectionId);
    
    if (!collection) {
      throw new Error('Collection not found');
    }
    
    return collection;
  }

  /**
   * Get items in a specific collection
   */
  async getCollectionItems(
    collectionId: string,
    page: number = 0,
    size: number = 20,
    sortBy: string = 'addedAt',
    sortDir: string = 'desc'
  ) {
    const response = await CollectionApiService.getCollectionItems(
      collectionId,
      page,
      size,
      sortBy,
      sortDir
    );
    
    // Return the response object instead of throwing errors
    // This allows the UI to handle success/error states properly
    return response;
  }

  /**
   * Get all items across all collections (for "All Saved" view)
   */
  async getAllItems() {
    // Get all collections
    const collections = await this.getCollections();
    
    // Find "All Saved" collection
    const allSavedCollection = collections.find(
      (c) => c.isDefault || c.name === 'All Saved'
    );
    
    if (allSavedCollection) {
      const response = await this.getCollectionItems(allSavedCollection.id);
      return response.success ? response.data || [] : [];
    }
    
    return [];
  }

  /**
   * Create a new collection
   */
  async createCollection(data: { name: string; description?: string }) {
    // Transform to match backend API expectation
    const apiData = {
      collectionName: data.name,
      description: data.description,
    };
    
    const response = await CollectionApiService.createCollection(apiData);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to create collection');
    }
    
    // Normalize API response: ensure 'name' field exists
    const collection = response.data;
    if (!collection.name && (collection as any).collectionName) {
      collection.name = (collection as any).collectionName;
    }
    
    return collection;
  }

  /**
   * Update a collection
   */
  async updateCollection(
    collectionId: string,
    data: { name?: string; description?: string }
  ) {
    // Transform to match backend API expectation
    const apiData: { collectionName?: string; description?: string } = {};
    if (data.name !== undefined) {
      apiData.collectionName = data.name;
    }
    if (data.description !== undefined) {
      apiData.description = data.description;
    }
    
    const response = await CollectionApiService.updateCollection(collectionId, apiData);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to update collection');
    }
    
    // Normalize API response: ensure 'name' field exists
    const collection = response.data;
    if (!collection.name && (collection as any).collectionName) {
      collection.name = (collection as any).collectionName;
    }
    
    return collection;
  }

  /**
   * Delete a collection
   */
  async deleteCollection(collectionId: string): Promise<void> {
    const response = await CollectionApiService.deleteCollection(collectionId);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to delete collection');
    }
  }

  /**
   * Add an item to a collection
   */
  async addItemToCollection(collectionId: string, item: any) {
    // Validate collectionId before making API call
    if (!collectionId || collectionId === 'undefined' || collectionId === 'null') {
      logger.error('[CollectionService] Invalid collectionId:', collectionId);
      throw new Error('Invalid collection ID. Please ensure the collection exists.');
    }
    
    const response = await CollectionApiService.addItemToCollection(
      collectionId,
      item
    );
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to add item to collection');
    }
    
    return response.data;
  }

  /**
   * Remove an item from a collection
   * Now uses brandId as the identifier (matching API spec)
   */
  async removeItemFromCollection(collectionId: string, itemIdOrBrandId: string): Promise<void> {
    // Validate collectionId before making API call
    if (!collectionId || collectionId === 'undefined' || collectionId === 'null') {
      logger.error('[CollectionService] Invalid collectionId:', collectionId);
      throw new Error('Invalid collection ID. Please ensure the collection exists.');
    }
    
    // If the second parameter looks like an item ID, fetch the item to get brandId
    // For backward compatibility with components that pass itemId
    let brandId = itemIdOrBrandId;
    
    // Check if this looks like an old-style item ID (contains underscore from timestamp)
    if (itemIdOrBrandId.includes('_')) {
      // Try to get the item to extract brandId
      try {
        const response = await this.getCollectionItems(collectionId);
        if (response.success && response.data) {
          const item = response.data.find((i) => i.id === itemIdOrBrandId);
          
          if (item) {
            brandId = item.brandId;
          } else {
            // If item not found, assume the parameter is already a brandId
            brandId = itemIdOrBrandId;
          }
        }
      } catch (error) {
        logger.warn('Could not fetch items to resolve brandId, using parameter as-is');
        brandId = itemIdOrBrandId;
      }
    }
    
    const response = await CollectionApiService.removeItemFromCollection(
      collectionId,
      brandId
    );
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to remove item');
    }
  }

  /**
   * Check which collections contain a specific item
   */
  async getItemCollections(brandId: string): Promise<string[]> {
    const response = await CollectionApiService.getItemCollections(brandId);
    
    if (!response.success) {
      logger.warn('Failed to check item collections:', response.error);
      return [];
    }
    
    return response.data || [];
  }

  /**
   * Get or create the default "All Saved" collection
   */
  async getOrCreateDefaultCollection() {
    const response = await CollectionApiService.getOrCreateDefaultCollection();
    
    if (!response.success || !response.data) {
      logger.error('[CollectionService] Failed to get default collection:', response.error);
      throw new Error(response.error || 'Failed to get default collection');
    }
    
    const collection:any = response.data;
    
    // Validate collection has required fields
    if (!collection.collectionId) {
      logger.error('[CollectionService] Collection missing ID:', collection);
      throw new Error('Invalid collection data: missing ID');
    }
    
    // Normalize API response: ensure 'name' field exists
    if (!collection.name && collection.collectionName) {
      collection.name = collection.collectionName;
    }
    
    return collection;
  }

  /**
   * Add item to multiple collections
   */
  async addItemToMultipleCollections(collectionIds: string[], item: any): Promise<void> {
    const response = await CollectionApiService.addItemToMultipleCollections(
      collectionIds,
      item
    );
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to add item to collections');
    }
  }

  /**
   * Initialize and sync collection counts
   * With API backend, this is handled server-side
   */
  async syncCollectionCounts(): Promise<void> {
    // No-op with API backend - counts are managed server-side
    // This method exists for backward compatibility
    logger.debug('[CollectionService] Sync counts is handled by API backend');
  }
}

// ============================================================================
// EXPORT SINGLETON INSTANCE
// ============================================================================

export const CollectionService = new CollectionServiceClass();
export default CollectionService;